import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../chrome/icons';
import { Spinner } from '../../ui/controls';
import type { Reference } from './contracts';
import type { ElementRef } from '../../services/elements';
import { ElementPartPicker } from './ElementPartPicker';
import { promptParts, refToken } from './promptMentions';
import { tileUrl } from '../content/tileUrl';

/*
 * The prompt with tags in it (Tarek, 14 Oct: "tag added references and elements inside my prompt
 * bar"), as Flow's Playground prompt does: `@` opens a list of what this run already holds —
 * pictures, clips, sounds, elements — and, below, the Elements library; a pick drops a pill
 * (its picture and name) where the `@` was. A library element is added to the run first.
 * The value is text with one token per pill (promptMentions.ts); the field rebuilds itself
 * only when the value changes from outside, so typing never moves the caret.
 */

export interface ElementHit { elementId: string; name: string; kind: string; thumbnail?: string }

/** A tag's picture, at the CDN's small size: it is drawn at 16 px. */
const pictureOf = (ref: Reference) => ref.role === 'audio' || ref.mediaKind === 'audio' ? '' : tileUrl(ref.role === 'video' || ref.mediaKind === 'video' ? ref.thumbnailUrl || '' : ref.url);
const glyphOf = (ref: Reference) => ref.role === 'audio' || ref.mediaKind === 'audio' ? 'reference-audio' : ref.role === 'video' || ref.mediaKind === 'video' ? 'format-video' : 'pictures';

function pill(token: string, name: string, ref: Reference | undefined) {
  const node = document.createElement('span');
  node.className = 'artwork-prompt-pill';
  node.contentEditable = 'false';
  node.dataset.token = token;
  const picture = ref ? pictureOf(ref) : '';
  if (picture) { const img = document.createElement('img'); img.src = picture; img.alt = ''; img.draggable = false; img.onerror = () => img.remove(); node.append(img); }
  const label = document.createElement('span');
  label.textContent = ref?.element ? `@${name}` : name;
  node.append(label);
  if (ref?.element) {
    // Which of its pictures go: a press opens the element's parts (ElementPartPicker).
    node.dataset.element = '';
    const count = ref.elementImageCount || 1, more = document.createElement('small');
    more.textContent = `${count}`; more.title = `${count} picture${count === 1 ? '' : 's'}`;
    const chevron = document.createElement('i'); chevron.setAttribute('aria-hidden', 'true');
    node.append(more, chevron);
    node.title = `${name} · ${count} picture${count === 1 ? '' : 's'} — choose which`;
  }
  if (!ref) node.classList.add('is-gone');
  return node;
}

/** Text and pills, back to the value. */
function serialize(root: HTMLElement) {
  let out = '';
  const walk = (node: Node) => node.childNodes.forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) { out += (child as Text).data.replace(/\u200b/g, '').replace(/\u00a0/g, ' '); return; }
    if (!(child instanceof HTMLElement)) return;
    if (child.dataset.token) { out += child.dataset.token; return; }
    if (child.tagName === 'BR') { out += '\n'; return; }
    walk(child);
    if (child.tagName === 'DIV' || child.tagName === 'P') out += '\n';
  });
  walk(root);
  return out.replace(/\n$/, '');
}

function build(root: HTMLElement, value: string, references: Reference[]) {
  root.replaceChildren(...promptParts(value).map(part => part.t === 'text' ? document.createTextNode(part.v)
    : pill(refToken({ id: part.id, name: part.name }), part.name, references.find(ref => ref.id === part.id))));
}

/** The `@word` the caret sits at the end of, if any. */
function mentionAtCaret(root: HTMLElement) {
  const selection = window.getSelection();
  if (!selection?.rangeCount || !selection.isCollapsed) return null;
  const node = selection.anchorNode;
  if (!node || node.nodeType !== Node.TEXT_NODE || !root.contains(node)) return null;
  const before = (node as Text).data.slice(0, selection.anchorOffset);
  const match = /(?:^|[\s(\u00a0])@([^\s@]{0,40})$/.exec(before);
  if (!match) return null;
  const start = selection.anchorOffset - match[1]!.length - 1;
  const range = document.createRange(); range.setStart(node, start); range.setEnd(node, selection.anchorOffset);
  const box = range.getBoundingClientRect();
  return { query: match[1]!, node: node as Text, start, end: selection.anchorOffset, box };
}

export function PromptField({ value, onChange, references, placeholder, label, maxLength, searchElements, onAddElement, onPickParts }: {
  value: string; onChange(value: string): void; references: Reference[];
  placeholder: string; label: string; maxLength?: number;
  /** The Elements library, searched as you type after `@`; absent = only what is already added. */
  searchElements?(query: string, signal: AbortSignal): Promise<ElementHit[]>;
  /** Adds a library element to the run; answers its reference so the pill can name it. */
  onAddElement?(hit: ElementHit): Promise<Reference | null>;
  /** An element tag's pictures changed (variant, looks, wardrobe…): swap its reference. */
  onPickParts?(ref: Reference, next: ElementRef): Promise<void>;
}) {
  const root = useRef<HTMLDivElement>(null), shown = useRef<string | null>(null);
  const [at, setAt] = useState<ReturnType<typeof mentionAtCaret>>(null);
  const [active, setActive] = useState(0), [hits, setHits] = useState<ElementHit[]>([]), [searching, setSearching] = useState(false), [adding, setAdding] = useState(false);
  const refs = useRef(references); refs.current = references;
  const [parts, setParts] = useState<{ ref: Reference; box: DOMRect } | null>(null), [applying, setApplying] = useState(false);

  // Rebuilt only when the value (or what its pills point at) changed from outside.
  const refsKey = references.map(ref => `${ref.id}:${ref.name}:${pictureOf(ref)}`).join('|');
  useLayoutEffect(() => {
    if (!root.current) return;
    if (shown.current === value && root.current.dataset.refs === refsKey) return;
    if (shown.current === value) {
      // Only what the pills point at changed (a reference added or removed): redraw the pills in
      // place, so the text and the caret stay where they are.
      root.current.querySelectorAll<HTMLElement>('[data-token]').forEach(node => {
        const part = promptParts(node.dataset.token!)[0];
        if (part?.t === 'ref') node.replaceWith(pill(node.dataset.token!, part.name, references.find(ref => ref.id === part.id)));
      });
    } else build(root.current, value, references);
    shown.current = value; root.current.dataset.refs = refsKey;
  }, [value, refsKey]);

  const query = at?.query.toLowerCase() ?? '';
  const added = at ? references.filter(ref => ref.name.toLowerCase().includes(query)) : [];
  const library = hits.filter(hit => !references.some(ref => ref.element?.elementId === hit.elementId));
  const rows: { ref?: Reference; hit?: ElementHit }[] = [...added.map(ref => ({ ref })), ...library.map(hit => ({ hit }))];
  useEffect(() => { setActive(0); }, [query, rows.length]);
  useEffect(() => {
    if (!at || !searchElements) { setHits([]); return; }
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(() => searchElements(at.query, controller.signal).then(found => { if (!controller.signal.aborted) setHits(found.slice(0, 6)); }).catch(() => undefined).finally(() => { if (!controller.signal.aborted) setSearching(false); }), 180);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [at?.query, !!at, searchElements]);

  function commit() {
    if (!root.current) return;
    const next = serialize(root.current);
    if (maxLength && next.length > maxLength && next.length > (shown.current?.length || 0)) { build(root.current, shown.current || '', refs.current); return; }
    shown.current = next; onChange(next);
  }
  function look() { setAt(root.current ? mentionAtCaret(root.current) : null); }

  function insert(ref: Reference, place = at) {
    const field = root.current; if (!field || !place) return;
    const { node, start, end } = place;
    if (!field.contains(node)) return;
    const after = node.splitText(start); after.data = after.data.slice(end - start);
    const tag = pill(refToken(ref), ref.name, ref), space = document.createTextNode(' ');
    node.parentNode!.insertBefore(tag, after); node.parentNode!.insertBefore(space, after);
    const selection = window.getSelection(), caret = document.createRange();
    caret.setStart(space, 1); caret.collapse(true); selection?.removeAllRanges(); selection?.addRange(caret);
    setAt(null); commit();
  }
  async function pick(row: { ref?: Reference; hit?: ElementHit }) {
    if (row.ref) { insert(row.ref); return; }
    if (!row.hit || !onAddElement || adding) return;
    const place = at; setAdding(true);
    try { const ref = await onAddElement(row.hit); if (ref) { root.current?.focus(); insert(ref, place); } }
    finally { setAdding(false); }
  }

  return <>
    <div ref={root} className={`artwork-prompt-field${value ? '' : ' is-empty'}`} contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label={label} data-placeholder={placeholder}
      aria-autocomplete="list" aria-expanded={!!at && rows.length > 0}
      onInput={() => { commit(); look(); }} onKeyUp={event => { if (!['ArrowUp', 'ArrowDown', 'Enter', 'Tab', 'Escape'].includes(event.key)) look(); }}
      onClick={event => {
        const tag = (event.target as HTMLElement).closest<HTMLElement>('[data-token]');
        const part = tag ? promptParts(tag.dataset.token!)[0] : undefined;
        const ref = part?.t === 'ref' ? references.find(item => item.id === part.id) : undefined;
        if (tag && ref?.element && onPickParts) { setParts({ ref, box: tag.getBoundingClientRect() }); return; }
        look();
      }} onBlur={() => setTimeout(() => setAt(null), 120)}
      onPaste={event => { event.preventDefault(); document.execCommand('insertText', false, event.clipboardData.getData('text/plain')); }}
      onDrop={event => { if (event.dataTransfer.types.includes('Files')) return; event.preventDefault(); }}
      onKeyDown={event => {
        if (at && rows.length) {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setActive(index => (index + (event.key === 'ArrowDown' ? 1 : rows.length - 1)) % rows.length); return; }
          if (event.key === 'Enter' || event.key === 'Tab') { event.preventDefault(); void pick(rows[active]!); return; }
        }
        if (at && event.key === 'Escape') { event.preventDefault(); setAt(null); return; }
        if (event.key === 'Enter') { event.preventDefault(); document.execCommand('insertText', false, '\n'); }
      }} />
    {parts && onPickParts && <ElementPartPicker element={parts.ref.element!} name={parts.ref.name} anchor={parts.box} busy={applying} onClose={() => setParts(null)}
      onApply={next => { setApplying(true); onPickParts(parts.ref, next).then(() => setParts(null)).finally(() => setApplying(false)); }} />}
    {at && (rows.length > 0 || searching || adding) && createPortal(<div className="menu-surface artwork-prompt-mentions" role="listbox" aria-label="Tag a reference"
      style={{ left: Math.min(at.box.left, window.innerWidth - 268), top: at.box.bottom + 6 }} onMouseDown={event => event.preventDefault()}>
      <div className="menu-list">
        {added.length > 0 && <div className="menu-heading">In this run</div>}
        {rows.map((row, index) => {
          const ref = row.ref, hit = row.hit;
          const picture = ref ? pictureOf(ref) : hit?.thumbnail && tileUrl(hit.thumbnail);
          return <Fragment key={ref ? ref.id : `hit-${hit!.elementId}`}>
            {index === added.length && <div className="menu-heading">Elements</div>}
            <div role="option" aria-selected={index === active} className="menu-item" data-active={index === active || undefined}
            onMouseEnter={() => setActive(index)} onClick={() => void pick(row)}>
            <span className="artwork-prompt-mention-art">{picture ? <img src={picture} alt="" /> : <Icon name={ref ? glyphOf(ref) : 'elements'} size={14} />}</span>
            <span className="artwork-prompt-mention-name">{ref?.element || hit ? `@${ref?.name ?? hit!.name}` : ref!.name}</span>
            <small>{hit ? 'Add' : ref?.element ? 'Element' : glyphOf(ref!) === 'format-video' ? 'Clip' : glyphOf(ref!) === 'reference-audio' ? 'Sound' : 'Picture'}</small>
            </div>
          </Fragment>;
        })}
        {(searching || adding) && <div className="artwork-prompt-mention-wait" role="status"><Spinner size={12} />{adding ? 'Adding element…' : 'Searching elements…'}</div>}
      </div>
    </div>, document.body)}
  </>;
}
